import { jsx, jsxs } from "react/jsx-runtime";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import dedent from "dedent";
import { forwardRef, useState, useRef, useImperativeHandle, useEffect } from "react";
import { useIsomorphicLayoutEffect } from "../../../hooks/useIsomorphicLayoutEffect.mjs";
import { Box } from "../../Box/Box.mjs";
import { BraidPortal } from "../../BraidPortal/BraidPortal.mjs";
import { useSpace } from "../../useSpace/useSpace.mjs";
import { animationTimeout } from "../animationTimeout.mjs";
import { transitionThreshold, triggerVars, backdrop, delayVisibility, invertPlacement, animation, popoverPosition, horizontalOffset } from "./Popover.css.mjs";
const zIndex = "notification";
const getPosition = (element) => {
  if (!element) {
    return void 0;
  }
  const rect = element.getBoundingClientRect();
  const { top, bottom, left, right, width } = rect;
  const { scrollX: scrollX2, scrollY, innerWidth } = window;
  return {
    // For `top`, we subtract this from the dynamic viewport height in `Popover.css.ts`
    // which can't be accessed from Javascript.
    top: top + scrollY,
    bottom: bottom + scrollY,
    left: left + scrollX2,
    right: innerWidth - right - scrollX2,
    width
  };
};
function clamp(min, preferred, max) {
  return Math.min(Math.max(preferred, min), max);
}
const PopoverContent = forwardRef(
  ({
    id,
    role,
    align = "left",
    width = "content",
    placement = "bottom",
    lockPlacement = false,
    offsetSpace = "none",
    delayVisibility: delayVisibility$1 = false,
    modal = true,
    open,
    onClose,
    triggerRef,
    enterFocusRef,
    children
  }, forwardedRef) => {
    const [triggerPosition, setTriggerPosition] = useState(void 0);
    const ref = useRef(null);
    useImperativeHandle(forwardedRef, () => ref.current);
    const [horizontalOffset$1, setHorizontalOffset] = useState(0);
    const [actualPlacement, setActualPlacement] = useState(placement);
    const showPopover = open && triggerPosition;
    const transitionThresholdInPx = useSpace().space[transitionThreshold];
    const alignmentAnchor = align === "center" ? "left" : align;
    useEffect(() => {
      const handleKeydown = (event) => {
        if (event.key === "Escape" || event.key === "Tab") {
          triggerRef.current?.focus();
          if (process.env.NODE_ENV !== "production") {
            if (!triggerRef.current) {
              console.error(
                dedent`
                  The triggerRef element could not be found.
                  Ensure it is being assigned to the Popover trigger, and is available on close.
                `
              );
            }
          }
          if (onClose) {
            onClose();
          }
        }
      };
      window.addEventListener("keydown", handleKeydown);
      return () => {
        window.removeEventListener("keydown", handleKeydown);
      };
    }, [onClose, triggerRef]);
    useEffect(() => {
      setTriggerPosition(getPosition(triggerRef.current));
    }, [triggerRef]);
    useEffect(() => {
      setTimeout(() => {
        if (!enterFocusRef) {
          return;
        }
        enterFocusRef.current?.focus();
        if (process.env.NODE_ENV !== "production") {
          if (!enterFocusRef.current) {
            console.error(
              dedent`
                The enterFocusRef element could not be found.
                Ensure it is being assigned to a child element of Popover, and is available on open.
                `
            );
          }
        }
      }, animationTimeout);
    }, [open, enterFocusRef, triggerRef]);
    useEffect(() => {
      const handleResize = () => {
        setTriggerPosition(getPosition(triggerRef.current));
      };
      window.addEventListener("resize", handleResize);
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(document.body);
      return () => {
        window.removeEventListener("resize", handleResize);
        resizeObserver.disconnect();
      };
    }, [triggerRef]);
    const handlePlacement = () => {
      const popoverBoundingRect = ref?.current?.getBoundingClientRect();
      if (!popoverBoundingRect) {
        return;
      }
      const triggerBoundingRect = triggerRef.current?.getBoundingClientRect();
      if (!triggerBoundingRect) {
        return;
      }
      const { height: popoverHeight } = popoverBoundingRect;
      const heightRequired = popoverHeight + transitionThresholdInPx;
      const fitsAbove = triggerBoundingRect.top >= heightRequired;
      const fitsBelow = window.innerHeight - triggerBoundingRect.bottom >= heightRequired;
      if (!fitsAbove && fitsBelow) {
        setActualPlacement("bottom");
      } else if (!fitsBelow && fitsAbove) {
        setActualPlacement("top");
      } else {
        setActualPlacement(placement);
      }
    };
    const handleHorizontalShift = () => {
      if (!triggerPosition) {
        return;
      }
      const popoverBoundingRect = ref?.current?.getBoundingClientRect();
      if (!popoverBoundingRect) {
        return;
      }
      const { width: popoverWidth } = popoverBoundingRect;
      const triggerCenter = triggerPosition.width && triggerPosition.left + triggerPosition.width / 2;
      const popoverLeft = align === "center" && triggerCenter ? triggerCenter - popoverWidth / 2 : triggerPosition.left;
      const clampedPopoverLeft = clamp(
        scrollX,
        popoverLeft,
        window.innerWidth + scrollX - popoverWidth
      );
      const triggerRightFromLeft = window.innerWidth - triggerPosition.right;
      const clampedTriggerRightFromLeft = clamp(
        scrollX + popoverWidth,
        triggerRightFromLeft,
        scrollX + window.innerWidth
      );
      if (alignmentAnchor === "right" && clampedTriggerRightFromLeft !== triggerPosition.right + horizontalOffset$1) {
        setHorizontalOffset(
          window.innerWidth - clampedTriggerRightFromLeft - triggerPosition.right
        );
      }
      if (alignmentAnchor === "left" && clampedPopoverLeft !== triggerPosition.left + horizontalOffset$1) {
        setHorizontalOffset(clampedPopoverLeft - triggerPosition.left);
      }
      return;
    };
    useIsomorphicLayoutEffect(() => {
      if (!showPopover) {
        return;
      }
      if (width !== "full") {
        handleHorizontalShift();
      }
      if (!lockPlacement) {
        handlePlacement();
      }
    }, [showPopover]);
    const triggerPositionVars = triggerPosition && {
      // Vertical positioning
      [triggerVars[actualPlacement]]: `${triggerPosition[actualPlacement]}`,
      // Horizontal positioning
      [triggerVars.left]: width === "full" || alignmentAnchor === "left" ? `${triggerPosition?.left}` : void 0,
      [triggerVars.right]: width === "full" || alignmentAnchor === "right" ? `${triggerPosition?.right}` : void 0,
      // Horizontal scroll offset
      [horizontalOffset]: width !== "full" ? `${horizontalOffset$1}` : "0"
    };
    if (!showPopover) {
      return null;
    }
    return /* @__PURE__ */ jsxs(BraidPortal, { children: [
      modal && /* @__PURE__ */ jsx(
        Box,
        {
          "data-testid": process.env.NODE_ENV !== "production" ? "backdrop" : void 0,
          onClick: (event) => {
            event.stopPropagation();
            event.preventDefault();
            if (onClose) {
              onClose();
            }
          },
          position: "fixed",
          zIndex,
          top: 0,
          left: 0,
          className: backdrop
        }
      ),
      /* @__PURE__ */ jsx(
        Box,
        {
          id,
          ref,
          role: role || void 0,
          tabIndex: -1,
          zIndex,
          position: "absolute",
          marginTop: actualPlacement === "bottom" ? offsetSpace : void 0,
          marginBottom: actualPlacement === "top" ? offsetSpace : void 0,
          style: triggerPositionVars && assignInlineVars(triggerPositionVars),
          className: {
            [popoverPosition]: true,
            [animation]: true,
            [invertPlacement]: actualPlacement === "bottom",
            [delayVisibility]: delayVisibility$1
          },
          children
        }
      )
    ] });
  }
);
const Popover = forwardRef(
  (props, forwardedRef) => {
    const { open, children, ...restProps } = props;
    if (!open) {
      return null;
    }
    return /* @__PURE__ */ jsx(PopoverContent, { open, ref: forwardedRef, ...restProps, children });
  }
);
export {
  Popover
};
